import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API } from '../services/api';
import { useAuth } from '../hooks/authContext';

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchMe = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const res = await API.get('/users/me');
        const payload = res.data?.user || res.data;

        if (isMounted) {
          setMe(payload);
        }
      } catch (err) {
        console.error('Error fetching /me:', err);
        if (isMounted) {
          setLoadError('Failed to load profile information.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMe();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleteError(null);

    if (confirmText.trim().toLowerCase() !== 'delete') {
      setDeleteError('You must type "delete" to confirm.');
      return;
    }

    const sure = window.confirm(
      'This will permanently delete your account and cannot be undone. Are you sure?'
    );
    if (!sure) return;

    try {
      setDeleting(true);
      await API.delete('/users/me');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError(
        err.response?.data?.message || 'Failed to delete account. Please try again.'
      );
      setDeleting(false);
    }
  };

  return (
    <div className="app">
      <Navbar />

      <main className="settings-main">
        <div className="settings-container">
          <h2 className="settings-title">Account Settings</h2>

          <section className="settings-section">
            <h3 className="settings-section-title">Profile</h3>

            {loading && <p className="settings-muted">Loading your data…</p>}
            {loadError && <p className="settings-error">{loadError}</p>}

            {!loading && !loadError && me && (
              <div className="settings-card">
                <div className="settings-row">
                  <span className="settings-label">ID</span>
                  <span className="settings-value">{me.id}</span>
                </div>
                <div className="settings-row">
                  <span className="settings-label">Email</span>
                  <span className="settings-value">{me.email}</span>
                </div>
                <div className="settings-row">
                  <span className="settings-label">Name</span>
                  <span className="settings-value">
                    {me.display_name || '—'}
                  </span>
                </div>
                <div className="settings-row">
                  <span className="settings-label">Role</span>
                  <span className="settings-value">
                    {me.role || user?.role || 'user'}
                  </span>
                </div>
                <div className="settings-row">
                  <span className="settings-label">Status</span>
                  <span className="settings-value">
                    {me.is_approved ? 'Approved' : 'Pending approval'}
                  </span>
                </div>
              </div>
            )}
          </section>

          <section className="settings-section danger">
            <h3 className="settings-section-title">Danger Zone</h3>
            <p className="settings-muted">
              Deleting your account is permanent and cannot be undone.
              All access to Columbus Property Analytics will be removed.
            </p>

            <form onSubmit={handleDelete} className="delete-form">
              <label className="settings-label">
                Type <code>delete</code> to confirm:
              </label>
              <input
                type="text"
                className="input-field settings-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="delete"
                disabled={deleting}
              />

              {deleteError && (
                <p className="settings-error">{deleteError}</p>
              )}

              <button
                type="submit"
                className="btn-danger"
                disabled={
                  deleting || confirmText.trim().toLowerCase() !== 'delete'
                }
              >
                {deleting ? 'Deleting…' : 'Delete my account'}
              </button>
            </form>
          </section>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
